import { computed, type Ref, ref } from 'vue'
import { type ListField, type ListState, useListState } from '@/composables/useListState'
import { getNestedValue } from '@/lib/utils'

export type ClientTableOptions<T> = {
    data: Ref<T[]>
    searchFields?: string[]
    defaultSort?: string
    defaultOrder?: 'ASC' | 'DESC'
    /** Opt into the shared list state (#633): search, sort, order and filters are kept under this key. */
    listKey?: string
}

export function useClientTable<T extends Record<string, unknown>>(options: ClientTableOptions<T>) {
    const page = ref(1)
    const limit = ref(25)
    const search = ref('')
    const sort = ref(options.defaultSort ?? '')
    const order = ref<'ASC' | 'DESC'>(options.defaultOrder ?? 'ASC')
    const filters = ref<Record<string, string>>({})

    /**
     * The choices a list page keeps (#633): search, sort, order and filters. Page number and page size are not kept:
     * a saved page can point past the end of a list that has changed since. A page with choices of its own beyond
     * these hands `useListState` these fields alongside its own; one with none passes `listKey`.
     */
    const listFields: Record<string, ListField> = {
        search: { ref: search },
        sort: { ref: sort },
        order: { ref: order },
        filters: { ref: filters },
    }
    const listState: ListState | null = options.listKey ? useListState(options.listKey, listFields) : null

    const filtered = computed(() => {
        let result = [...options.data.value]

        // Apply search
        if (search.value && options.searchFields) {
            const searchLower = search.value.toLowerCase()
            result = result.filter(item =>
                options.searchFields?.some(field => {
                    const value = getNestedValue(item, field)
                    return String(value ?? '')
                        .toLowerCase()
                        .includes(searchLower)
                }),
            )
        }

        // Apply filters
        for (const [key, value] of Object.entries(filters.value)) {
            if (value) {
                result = result.filter(item => {
                    const itemValue = getNestedValue(item, key)
                    return String(itemValue).toLowerCase() === value.toLowerCase()
                })
            }
        }

        // Apply sorting
        if (sort.value) {
            result.sort((a, b) => {
                const aValue = getNestedValue(a, sort.value)
                const bValue = getNestedValue(b, sort.value)
                const comparison = String(aValue ?? '').localeCompare(String(bValue ?? ''))
                return order.value === 'ASC' ? comparison : -comparison
            })
        }

        return result
    })

    const paginated = computed(() => {
        const start = (page.value - 1) * limit.value
        const end = start + limit.value
        return filtered.value.slice(start, end)
    })

    const total = computed(() => filtered.value.length)
    const pages = computed(() => Math.ceil(filtered.value.length / limit.value))

    /**
     * The rows/paging/search/sort/filter props a `<DataTable>` reads, plus the `update:*`
     * listeners that write changes back to this table's state — ready to `v-bind`. Every view
     * using `useClientTable` wired these same six props and six listeners by hand; `v-bind` keeps
     * that wiring in one place instead of repeating it per view.
     */
    const tableProps = computed(() => ({
        rows: paginated.value,
        total: total.value,
        page: page.value,
        limit: limit.value,
        pages: pages.value,
        search: search.value,
        sort: sort.value,
        order: order.value,
        filters: filters.value,
        ...(listState ? { listState } : {}),
        'onUpdate:page': (value: number) => {
            page.value = value
        },
        'onUpdate:limit': (value: number) => {
            limit.value = value
        },
        'onUpdate:search': (value: string) => {
            search.value = value
        },
        'onUpdate:sort': (value: string) => {
            sort.value = value
        },
        'onUpdate:order': (value: 'ASC' | 'DESC') => {
            order.value = value
        },
        'onUpdate:filters': (value: Record<string, string>) => {
            filters.value = value
        },
    }))

    return {
        page,
        limit,
        search,
        sort,
        order,
        filters,
        paginated,
        total,
        pages,
        tableProps,
        listFields,
        listState,
    }
}
